import { useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  usePackingList,
  useDeletePackingList,
  useCreateDnFromPacking,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { buildPackingListHtml, printPackingList } from "@/lib/printPackingList";
import { exportPackingListCsv } from "@/lib/exportDocument";
import { formatDate } from "@/domain/datetime";
import { getLocalizedError } from "@/lib/api";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";
import { DocumentExportDialog } from "@/components/domain/DocumentExportDialog";
export function PackingListDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { search: context } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = useCan();
  const result = usePackingList(id);
  const pl = result.data;
  const remove = useDeletePackingList();
  const delivery = useCreateDnFromPacking();
  const [confirm, setConfirm] = useState<"archive" | "delivery" | null>(null);
  const [preview, setPreview] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const busy = remove.isPending || delivery.isPending;
  usePageCrumbs(pl ? [{ label: pl.plNo }] : result.error ? [{ label: t("common.unavailable") }] : [], `/packing-lists${context}`);
  if (result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (result.error || !pl)
    return (
      <PageBody>
        <ErrorState
          message={t("packingLists.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  const related = pl.deliveryNotes ?? [];
  const cartons = new Map<string, typeof pl.items>();
  for (const it of pl.items) {
    const key = it.ctnNo ?? "";
    cartons.set(key, [...(cartons.get(key) ?? []), it]);
  }
  const quantities = new Map<string, number>();
  for (const it of pl.items) {
    const key = it.unit || "—";
    quantities.set(key, (quantities.get(key) ?? 0) + it.quantity);
  }
  const kg = (n: number | null | undefined) =>
    n == null
      ? "—"
      : n.toLocaleString("en-US", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        });
  const action = async () => {
    if (lock.current) return;
    lock.current = true;
    setError(null);
    try {
      if (confirm === "archive") {
        await remove.mutateAsync(pl.id);
        navigate(`/packing-lists${context}`);
      } else {
        if(related.length)navigate(`/delivery-notes/${related[0]!.id}`);
        else navigate(`/delivery-notes/new?refType=packing&sourceId=${pl.id}`);
      }
      setConfirm(null);
    } catch (err) {
      setError(getLocalizedError(err, t, "packingLists.errorSave"));
      setConfirm(null);
    } finally {
      lock.current = false;
    }
  };
  return (
    <PageBody>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-mono text-xl font-semibold">{pl.plNo}</h1>
          {pl.piId && (
            <Link
              className="mt-1 inline-block font-mono text-sm text-brand underline"
              to={`/proforma-invoices/${pl.piId}`}
            >
              {t("common.fromPi")} {pl.piNo}
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPreview(true)}
          >
            {t("packingWorkspace.preview")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-haspopup="dialog"
            onClick={() => setExportOpen(true)}
          >
            {t("common.export")}
          </Button>
          {user && can("records:update") && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || related.length > 0}
              onClick={() => navigate(`/packing-lists/${pl.id}/edit${context}`)}
            >
              {t("common.edit")}
            </Button>
          )}
          {user && can("records:create") && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => setConfirm("delivery")}
            >
              {t(
                related.length
                  ? "invoiceWorkspace.openDelivery"
                  : "invoiceWorkspace.createDelivery",
              )}
            </Button>
          )}
          {user && can("records:delete") && (
            <Button
              variant="danger"
              size="sm"
              disabled={busy || related.length > 0}
              onClick={() => setConfirm("archive")}
            >
              {t("packingWorkspace.archive")}
            </Button>
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      )}
      {related.length > 0 && (
        <Panel className="mt-3 p-3">
          <p className="text-xs text-ink-soft">
            {t("packingWorkspace.packing_delivery_locked")}
          </p>
          <div className="mt-2 flex gap-3">
            {related.map((d) => (
              <Link
                className="font-mono text-sm text-brand underline"
                to={`/delivery-notes/${d.id}`}
                key={d.id}
              >
                {d.dnNo}
              </Link>
            ))}
          </div>
        </Panel>
      )}
      {pl.pProcess&&pl.items.some(it=>!it.lotNumber?.trim())&&<p className="mt-3 text-sm text-warn">{t("packingWorkspace.packing_lot_required")}</p>}
      {!pl.weightsComplete && (
        <p className="mt-3 text-sm text-warn">
          {t("packingWorkspace.unweighed")}
        </p>
      )}
      {pl.cartonConflicts && (
        <p className="mt-2 text-sm text-warn">
          {t("packingWorkspace.resolveConflicts")}
        </p>
      )}
      <Panel className="mt-4 p-4">
        <dl className="grid gap-4 sm:grid-cols-3">
          {[
            [t("lot.pProcess"),t(pl.pProcess?"invoiceWorkspace.pProcessOn":"invoiceWorkspace.pProcessOff")],
            [t("common.customer"), pl.customerName],
            [t("common.shipTo"), pl.shipTo],
            [t("packingLists.invoiceNo"), pl.invoiceNo],
            [t("common.date"), formatDate(pl.date)],
            [t("packingLists.shippingMode"), pl.shippingMode],
            [t("pi.respBy"), pl.respBy],
            [t("packingLists.attention"), pl.attention],
            [t("packingLists.shippingMarks"), pl.shippingMarks],
          ].map(([label, value]) => (
            <div className="min-w-0" key={label}>
              <dt className="text-2xs uppercase text-ink-faint">{label}</dt>
              <dd className="mt-1 whitespace-pre-line break-words text-sm">
                {value || "—"}
              </dd>
            </div>
          ))}
        </dl>
      </Panel>
      {[...cartons].map(([ctn, items]) => (
        <Panel className="mt-3" key={ctn}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line p-3">
            <h2 className="font-mono text-sm font-semibold">{ctn || "—"}</h2>
            <div className="flex flex-wrap gap-4 text-xs">
              <span>
                {t("packingLists.netWeight")}:{" "}
                <span className="font-mono">{kg(items[0]?.nwKgs)}</span>
              </span>
              <span>
                {t("packingLists.grossWeight")}:{" "}
                <span className="font-mono">{kg(items[0]?.gwKgs)}</span>
              </span>
              <span>{items[0]?.measurementCm || "—"}</span>
            </div>
          </div>
          <TableShell className="rounded-none border-0">
            <thead>
              <tr>
                <Th pin>
                  {t("common.code")} / {t("common.subRef")}
                </Th>
                <Th>{t("common.description")}</Th>
                <Th>{t("common.colorSize")}</Th>
                <Th>{t("packingLists.customerPo")}</Th>
                <Th>{t("lot.number")}</Th>
                <Th align="right">{t("common.qty")}</Th>
                <Th>{t("common.unit")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <Tr key={it.id ?? it.lineNo}>
                  <Td pin className="font-mono text-xs">
                    {it.itemNo || "—"}
                    {it.subRef ? ` / ${it.subRef}` : ""}
                  </Td>
                  <Td>
                    <p className="max-w-72 whitespace-normal text-xs">
                      {it.description || "—"}
                    </p>
                  </Td>
                  <Td className="text-xs">
                    {[it.color, it.size].filter(Boolean).join(" · ") || "—"}
                  </Td>
                  <Td className="font-mono text-xs">{it.customerPo || "—"}</Td>
                  <Td className="font-mono text-xs">{it.lotNumber || "—"}</Td>
                  <Td align="right" className="font-mono">
                    {it.quantity}
                  </Td>
                  <Td className="text-xs">{it.unit || "—"}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </Panel>
      ))}
      <Panel className="mt-3 flex flex-wrap justify-between gap-4 p-4">
        <div>
          <span className="text-xs text-ink-faint">{t("common.cartons")}</span>
          <p className="font-mono">{pl.totalCtns}</p>
        </div>
        <div>
          <span className="text-xs text-ink-faint">
            {t("packingWorkspace.quantityByUnit")}
          </span>
          {[...quantities].map(([unit, qty]) => (
            <p className="font-mono" key={unit}>
              {qty.toLocaleString()} {unit}
            </p>
          ))}
        </div>
        <div>
          <span className="text-xs text-ink-faint">
            {t("packingLists.netWeight")}
          </span>
          <p className="font-mono">
            {pl.weightsComplete ? kg(pl.totalNw) : "—"}
          </p>
        </div>
        <div>
          <span className="text-xs text-ink-faint">
            {t("packingLists.grossWeight")}
          </span>
          <p className="font-mono">
            {pl.weightsComplete ? kg(pl.totalGw) : "—"}
          </p>
        </div>
      </Panel>
      <ConfirmDialog
        open={confirm !== null}
        title={t(
          confirm === "archive"
            ? "packingWorkspace.archive"
            : "invoiceWorkspace.createDelivery",
        )}
        description={
          confirm === "archive"
            ? t("packingLists.confirmDelete", { number: pl.plNo })
            : `${t("packingWorkspace.deliveryReview")}${!pl.weightsComplete ? " " + t("packingWorkspace.unweighed") : ""}`
        }
        confirmLabel={t(
          confirm === "archive"
            ? "packingWorkspace.archive"
            : related.length
              ? "invoiceWorkspace.openDelivery"
              : "invoiceWorkspace.createDelivery",
        )}
        cancelLabel={t("common.cancel")}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void action()}
      />
      {exportOpen && (
        <DocumentExportDialog
          key={pl.id}
          documentNo={pl.plNo}
          onCsv={() => exportPackingListCsv(pl, t)}
          onPdf={() => { setExportOpen(false); setPreview(true); }}
          onExcel={async () => {
            const { exportPackingListExcel } = await import("@/lib/exportPackingListExcel");
            return exportPackingListExcel(pl);
          }}
          onClose={() => setExportOpen(false)}
        />
      )}
      {preview && (
        <Modal
          className="max-w-6xl"
          title={`${t("packingWorkspace.preview")} · ${pl.plNo}`}
          closeLabel={t("common.close")}
          onClose={() => setPreview(false)}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 p-3">
            <SaveAsPdfHint />
            <Button onClick={() => printPackingList(pl)}>
              {t("common.print")}
            </Button>
          </div>
          <iframe
            title={t("packingWorkspace.preview")}
            sandbox="allow-same-origin"
            srcDoc={buildPackingListHtml(pl)}
            className="min-h-[70dvh] w-full bg-surface"
          />
        </Modal>
      )}
    </PageBody>
  );
}
